import { useEffect, useId, useState } from 'react';
import { Save } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { ApiRequestError } from '@/lib/apiClient';
import type { SalaryComponent } from '@/types/payroll';
import { payrollApi } from '../api/payroll.api';
import { SALARY_CALCULATION_OPTIONS, SALARY_KIND_OPTIONS } from '../constants';
import { EMPTY_PAY_HEAD, payHeadSchema, type PayHeadFormValues } from '../schemas/payroll.schema';

export interface PayHeadFormModalProps {
  /** The head being corrected, or null to add one. */
  head: SalaryComponent | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (head: SalaryComponent) => void;
}

type FieldErrors = Partial<Record<keyof PayHeadFormValues, string>>;

/**
 * Adding or correcting one pay head.
 *
 * Kind and code are fixed once a head exists — the server refuses to change
 * them, because flipping an earning into a deduction would reverse it on every
 * salary that carries it. So on an edit they are shown, not offered.
 */
export function PayHeadFormModal({ head, isOpen, onClose, onSaved }: PayHeadFormModalProps) {
  const formId = useId();
  const [values, setValues] = useState<PayHeadFormValues>(EMPTY_PAY_HEAD);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setValues(
      head
        ? {
            name: head.name,
            code: head.code,
            kind: head.kind,
            calculation: head.calculation,
            defaultValue: head.defaultValue ?? '',
            isStatutory: head.isStatutory,
            isActive: head.isActive,
          }
        : EMPTY_PAY_HEAD,
    );
    setFieldErrors({});
    setFormError(null);
  }, [head, isOpen]);

  function set<K extends keyof PayHeadFormValues>(key: K, value: PayHeadFormValues[K]) {
    setValues((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const parsed = payHeadSchema.safeParse(values);

    if (!parsed.success) {
      const errors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof PayHeadFormValues | undefined;
        if (field && !errors[field]) errors[field] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setIsSaving(true);
    const { code, kind, isActive, ...shared } = parsed.data;

    try {
      const saved = head
        ? await payrollApi.updateComponent(head.uuid, { ...shared, isActive })
        : await payrollApi.createComponent({ ...shared, code, kind });
      onSaved(saved);
    } catch (caught) {
      setFormError(
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  const isPercent = values.calculation === 'PERCENT_OF_BASIC';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={head ? `Edit ${head.name}` : 'Add a pay head'}
      description="Pay heads are what every salary is built from. The default only pre-fills a new salary — changing it never changes a salary already set."
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" form={formId} isLoading={isSaving} leftIcon={<Save className="h-4 w-4" />}>
            {head ? 'Save changes' : 'Add pay head'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not save this pay head">
            {formError}
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Input
              label="Name"
              placeholder="e.g. House Rent Allowance"
              value={values.name}
              error={fieldErrors.name}
              onChange={(event) => set('name', event.target.value)}
            />
          </div>
          <Input
            label="Code"
            placeholder="e.g. HRA"
            value={values.code}
            error={fieldErrors.code}
            disabled={Boolean(head)}
            hint={head ? 'Fixed once added.' : 'Printed on the payslip.'}
            onChange={(event) => set('code', event.target.value.toUpperCase())}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Type"
            options={SALARY_KIND_OPTIONS}
            value={values.kind}
            error={fieldErrors.kind}
            disabled={Boolean(head)}
            hint={head ? 'Fixed once added.' : undefined}
            onChange={(event) => set('kind', event.target.value as PayHeadFormValues['kind'])}
          />
          <Select
            label="Worked out as"
            options={SALARY_CALCULATION_OPTIONS}
            value={values.calculation}
            error={fieldErrors.calculation}
            onChange={(event) =>
              set('calculation', event.target.value as PayHeadFormValues['calculation'])
            }
          />
        </div>

        <Input
          label={isPercent ? 'Default percentage of Basic' : 'Default amount (₹)'}
          inputMode="decimal"
          placeholder={isPercent ? 'e.g. 40' : 'e.g. 1600'}
          value={values.defaultValue ?? ''}
          error={fieldErrors.defaultValue}
          hint="Optional. Leave blank when it differs for everyone."
          onChange={(event) => set('defaultValue', event.target.value)}
        />

        <div className="flex flex-col gap-2.5">
          <Checkbox
            label="Statutory (PF, ESI, Professional Tax, TDS)"
            checked={values.isStatutory}
            onChange={(event) => set('isStatutory', event.target.checked)}
          />
          {head && (
            <Checkbox
              label="In use — offered on new salaries"
              checked={values.isActive}
              onChange={(event) => set('isActive', event.target.checked)}
            />
          )}
        </div>
      </form>
    </Modal>
  );
}
